import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseComponent, WmsDialogBodyComponent, WmsDialogHeaderComponent, WmsTypeaheadComponent } from '@wms/app/components';
import { DialogService, NotificationService, ServiceFactoryService } from '@wms/app/services';
import { CopilotService } from '@wms/copilot';
import { FormsModule } from '@angular/forms';
import { MarkupManagerService, VersionManagerPipe } from '@wms/core';
import { forEach, get, isEmpty, orderBy } from 'lodash-es';
import { runtimeEventManager } from '@wm/core';
import { BaseVariableService, VARIABLE_CONSTANTS, VariableCrudManagerService } from '@wms/app/variables';
import { WmsWorkspaceManagerService } from '@wms/app/workspace';

@Component({
  selector: 'wms-copilot-modal-content',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    WmsDialogHeaderComponent,
    WmsDialogBodyComponent,
    VersionManagerPipe,
    WmsTypeaheadComponent
  ],
  templateUrl: './copilot-modal-content.component.html',
  styleUrls: ['./copilot-modal-content.component.less']
})
export class CopilotModalContentComponent extends BaseComponent implements OnInit {

  @Output() dialogSize = new EventEmitter<string>();
  promptText: string = '';
  currentIndex: number = 0;
  highlightedIndex: number = 0;

  prompts: any[] = [];
  markupContent: any[] = [];
  activeWidget: any;
  generatedResponse: any[] = [];
  dataSource: any = [];
  parentWidgetId: string | null | undefined;

  insertableElements: any[] = ['wm-gridcolumn', 'wm-page-content', 'wm-container'];
  widgetsForBinding: any[] = ['wm-form', 'wm-liveform', 'wm-wizard'];
  isValidWidgetType: boolean;
  context: string;
  operations: any = [];
  services: any = [];
  operationId: string = "";
  operationName: string = "";
  serviceId: string;
  copilotIconSrc: string = 'assets/images/copilot/copilot-plain-glitter.png';

  constructor(private dialogService: DialogService, public copilotService: CopilotService, private notificationService: NotificationService, private markupManagerService: MarkupManagerService,
    private varManagerService: VariableCrudManagerService, private baseVariableService: BaseVariableService,
    private serviceFactory: ServiceFactoryService, private wsManagerService: WmsWorkspaceManagerService) {
    super();
  }

  ngOnInit() {
    this.setContext();
    if (this.wsManagerService.activeWSCategory === "PAGE") {
      this.setupPageWsContext();
    }
  }

  /**
   * Sets the context based on the active workspace's details, specifically the current tab.
   * Updates the `context` property with the workspace's tab information.
   */
  setContext() {
    this.context = this.wsManagerService.activeWS.details.tab;
  }

  setupPageWsContext() {
    this.activeWidget = (this.markupManagerService.getActiveCanvasEl()?.[0] as any)?.widget;
    const widgetType = this.activeWidget.widgetType;
    this.isValidWidgetType = this.insertableElements.includes(widgetType) || this.widgetsForBinding.includes(widgetType);
    if (this.widgetsForBinding.includes(widgetType))
      this.prompts = ["Bind the form with api"];
    else
      this.prompts = ["Create a form for adding a beneficiary", "Create a form user login", "Create a form for new user registration"];
  }

  /**
  * Fetches the available services and filters only OpenAPI services.
  * Populates the `dataSource` array with the OpenAPI services.
  */
  fetchServices() {
    (this.serviceFactory.getServicesWithType() as Promise<any>).then((services: any[]) => {
      this.services = services;
      const serviceType = VARIABLE_CONSTANTS.SERVICE_TYPE_OPEN_API;
      this.dataSource = services.filter(service => service.type === serviceType);
    })
  }

  /**
   * Handles the selection of a service. Fetches its operations and data types.
   * @param selectedService - The service object selected by the user.
   */
  onServiceSelect(service: any) {
    this.serviceId = service.name;
    (this.serviceFactory.getServiceOperations(this.serviceId) as Promise<any>).then((operations: any[]) => {
      let opController: string, tempOperations: any = [];
      operations = orderBy(operations, 'name');
      forEach(operations, (operation: any) => {
        opController = operation.controller ? operation.controller.replace(/Controller$/, '') : '';
        tempOperations.push({
          type: opController,
          operationId: operation.operationId,
          name: operation.name.replace(this.serviceId + '_', '').replace(operation.entityName + '_', ''),
        });
      });
      this.operations = tempOperations;
    });
  }

  /**
   * Generates suggestions based on the user's input.
   */
  generateSuggestions() {
    (this.copilotService.generateContent(this.createPromptObject()) as Promise<any>).then((response: any) => {
      this.generatedResponse = response;
      this.generatedResponse.forEach((resource: any) => {
        if (isEmpty(resource.content)) {
          return;
        }
        else if (resource.type === 'MARKUP' && resource.action === 'CREATE') {
          this.markupContent = resource.content.widgetContent;
          this.setParentWidgetId();
          this.addMarkup(true);
          this.copilotService.setActiveScreen('results-screen');
        }
        else if (resource.type === 'VARIABLE' && resource.action === 'UPDATE') {
          this.generateBindings(resource.content.mapping);
          this.dialogService.close('copilot-modal');
          this.copilotService.setActiveScreen('copilot');
        }
      });
    }).catch((errorObj) => {
      this.notificationService.show('error', this.locale['MESSAGE_ERROR_TITLE'], get(errorObj, 'error.errors.error[0].parameters[0]'));
    })
  }

  /**
   * Creates and returns an object representing the prompt request.
   * @returns An object containing the user's query, copilot intent, and copilot metadata.
   */
  createPromptObject() {
    let promptObject: any = {
      "userQuery": this.promptText,
      "copilotIntent": {
        "context": this.context,
        "type": this.widgetsForBinding.includes(this.activeWidget.widgetType) ? "GENERATE_BINDING" : "CREATE"
      },
      "copilotMetadata": {
        "widgetId": this.activeWidget.widgetType,
        "pageName": this.activeWidget.pageName,
        "operationId": this.operationId,
        "serviceId": this.serviceId
      }
    };

    if (this.widgetsForBinding.includes(this.activeWidget.widgetType))
      promptObject.copilotMetadata["widgetName"] = this.activeWidget.name;

    return promptObject;
  }

  /**
   * Generates bindings for variable based on a service and operation provided
   */
  generateBindings(bindingsMap: any) {
    let variable: any = {};
    this.serviceFactory.getServiceOperationParams(this.serviceId, this.operationId).then((response: any) => {
      variable.isList = response.isList;
      variable.type = response.return.typeRef;
      variable.operationType = response.operationType;
      variable.controller = response.controller;
      variable.dataBinding = [];
      variable.category = "wm.ServiceVariable";
      variable.service = this.serviceId;
      variable.operation = this.operationId;
      variable.operationId = this.operationId;
      variable.serviceType = this.serviceFactory.getServiceObjectByName(this.serviceId).type;
      variable.autoUpdate = false;
      variable.name = this.baseVariableService.generateUniqueName(variable.category, undefined, undefined);
      forEach(response.parameter, (param: any) => {
        const paramPrefix = param.name;
        if (param.parameterType !== 'OUT' && bindingsMap) {
          bindingsMap.forEach((binding: any) => {
            const bindingPrefix = binding.attrKey.split('.')[0];
            if (bindingPrefix === paramPrefix) {
              variable.dataBinding.push({
                target: binding.attrKey,
                type: param.typeRef,
                isList: param.isList,
                format: param.format,
                value: binding.attrValue
              });
            }
          });
        }
      });
      this.createVariable(variable);
      this.varManagerService.invokeSave();
      this.bindVariableToEvent(variable.name);
    });
  }

  /**
   * Binds a variable to a specific event on the active widget.
   * @param variableName - The name of the variable to bind.
   */
  bindVariableToEvent(variableName: string) {
    let bindValue = "Variables." + variableName + ".invoke()",
      attrName = '';
    if (this.activeWidget.widgetType === 'wm-form' || this.activeWidget.widgetType === 'wm-liveform') {
      attrName = 'on-submit';
    }
    else if (this.activeWidget.widgetType === 'wm-wizard') {
      attrName = 'on-Done'
    }
    runtimeEventManager.emit('update-widget-property', { prop: attrName, value: bindValue });
  }

  setParentWidgetId() {
    this.parentWidgetId = this.markupManagerService.getActiveMarkupEl()?.[0].getAttribute('widgetid');
  }

  /**
   * Adds the suggested markup content to the active element.
   * @param persistLastChild - Whether to keep the last child of the active element.
   */
  addMarkup(persistLastChild?: boolean) {
    if (!persistLastChild) {
      this.removeMarkup();
    }
    const newContent = this.markupContent[this.currentIndex];
    this.eventManager.emitOnCanvasAdd({
      'parentId': this.parentWidgetId,
      'templateEl': $(newContent)
    });
  }

  onModalClose() {
    if (this.copilotService.currentScreen === 'results-screen') {
      this.removeMarkup();
    }
    this.dialogService.close('copilot-modal');
    this.copilotService.setActiveScreen('copilot');
  }

  saveSuggestion() {
    this.generatedResponse.forEach((resource: any) => {
      if ((resource.type === 'JAVASCRIPT' || resource.type === 'STYLE') && resource.content !== '') {
        runtimeEventManager.emit('update-resource-content', resource);
      }
      else if (resource.type === 'VARIABLE' && !isEmpty(resource.content.variables)) {
        const variables = resource.content.variables;
        variables.forEach((variable: any) => {
          this.createVariable(variable);
        });
        this.varManagerService.invokeSave();
      }
    });
    this.dialogService.close('copilot-modal');
    this.copilotService.setActiveScreen('copilot');
  }

  createVariable(variable: any) {
    let options: any = {};
    if (variable.category === 'wm.ServiceVariable') {
      options = {
        'service': variable.service,
        'name': variable.name,
        'operationType': variable.operationType,
        'operation': variable.operation,
        'returnType': variable.type,
        'serviceType': variable.serviceType,
        'startUpdate': variable.startUpdate,
        'autoUpdate': variable.autoUpdate,
        'operationId': variable.operationId,
        'controller': variable.controller,
        'isList': variable.isList,
        'dataBinding': variable.dataBinding
      };
      this.baseVariableService.createServiceVariable(options, undefined, undefined);
    }
  }

  removeMarkup() {
    const activeCanvasEl: any = this.markupManagerService.getActiveCanvasEl(),
      widgetId = this.markupManagerService.getActiveCanvasEl()?.[0].getAttribute('widgetid');
    this.eventManager.emitOnCanvasDelete({
      'widgetId': widgetId,
      'element': $(activeCanvasEl)
    });
  }

  navigateBackward() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.addMarkup();
    }
  }

  navigateForward() {
    if (this.currentIndex < this.markupContent.length - 1) {
      this.currentIndex++;
      this.addMarkup();
    }
  }

  /**
   * Clears the selected service and operation details.
   */
  clearSource() {
    this.serviceId = "";
    this.operationId = "";
    this.operationName = "";
  }

  /**
   * Clears only the selected operation details.
   */
  clearOperation() {
    this.operationId = "";
  }

  /**
   * Sets the selected operation details when an operation is chosen.
   * @param operation - The selected operation object containing operationId and name.
   */
  onOperationSelect(operation: any) {
    this.operationId = operation.operationId;
    this.operationName = operation.name;
  }

  /**
   * Activates the 'data-configuration' screen and fetches available services.
   */
  enableDataConfigurationStep() {
    this.copilotService.setActiveScreen('data-configuration');
    this.fetchServices();
  }

  /**
   * Handles user selection of a suggestion, sets the prompt text,
   * and navigates to the data configuration step.
   * @param suggestion - The selected suggestion string.
   */
  handleSuggestionClick(suggestion: string) {
    this.promptText = suggestion;
    this.enableDataConfigurationStep();
  }

}
